import React, { useCallback, useEffect, useState } from 'react';
import PageWithAuth from '../../../../components/PageWithAuth';
import styled from 'styled-components';
import Squiggly from '../../../marketing/components/Squiggly';
import { up, down } from 'styled-breakpoints';
import GiftDropForm from '../../components/GifDropForm';
import SuccessfulGift from '../../components/SuccessfulGift';
import Alert, { AlertType } from '../../../../components/Alert';
import { Elements, StripeProvider } from 'react-stripe-elements';
import { fontsForStripe } from '../../../style-quiz/components/steps/ShippingAndBillingAndConfirmStep/stripe-utils';
import { stripePublicKey } from '../../../../constants';
import pagesMeta from '../../../../constants/pages-meta';
import { User } from '../../../../types';
import { useRouter } from 'next/router';
import { GiftThankYouState } from '../../types';
import { EMPTY_THANKYOU_STATE } from '../../constants';

export const StyledSquiggly = styled(Squiggly)`
  margin: 15px 0 29px;
`;

export const PageContainer = styled.div`
  padding-bottom: 50px;
  padding-top: 100px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  & > div {
    padding: 20px 40px;
    ${up('md')} {
      max-width: 800px;
    }
  }
`;

const SummaryText = styled.div`
  opacity: 0.4;
  margin: 20px 40px;
  ${down('sm')} {
    margin: 20px 0;
  }
  span {
    font-style: italic;
  }
  p {
    margin-bottom: 15px;
    line-height: 1.64;
  }
`;

export const Title = styled.h5.attrs({ className: 'heading-s' })`
  padding: 30px 0;
  text-align: center;
`;

const GiftDropPage = () => {
  const [stripe, setStripe] = useState(null);
  const { reload } = useRouter();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.Stripe && setStripe(window.Stripe(stripePublicKey));
  }, []);

  const [state, setState] = useState<GiftThankYouState>(EMPTY_THANKYOU_STATE);

  const backHandler = useCallback(() => {
    /**
     *  Same route to start gifting again, reload to get a fresh authenticated page
     *  in case of a new authenticated user
     */
    reload();
  }, []);

  return (
    <PageWithAuth showWaitList title={pagesMeta.gift.title} description={pagesMeta.gift.description}>
      <StripeProvider stripe={stripe}>
        <Elements fonts={fontsForStripe}>
          <PageContainer>
            {!state.success ? (
              <>
                <Title>Dopple Gift Card Checkout</Title>
                <StyledSquiggly />
                <SummaryText>
                  <p>
                    The Dopple Gift Drop is the perfect prezzie for stylish parents and their mini-me kiddos... without
                    you having to lift a finger!{' '}
                    <span>And it&apos;s so much better than a gift card when you need a last minute present.</span>
                  </p>
                  <p>
                    Just tell us how much you want to give and who you&apos;d like us to send it to, and Dopple&apos;s
                    mini fashion experts do the rest. Your recipient will get an email from us with a link to take the
                    Style Quiz. Once they take it, we&apos;ll create the perfect &quot;Drop&quot; of personalized
                    outfits from the coolest kids brands and send it to them.
                  </p>

                  <p>
                    Et voila! You win best-gift-giver-ever, every time. (They even get to return anything in their Drop
                    they don&apos;t want to keep and get credit toward their future Drops!)
                  </p>
                </SummaryText>
                <GiftDropForm state={state} updateState={setState} />
                {state.error && <Alert type={AlertType.ERROR}>{state.error}</Alert>}
              </>
            ) : (
              <SuccessfulGift
                state={state}
                recipientName={state.recipientName}
                recipientEmail={state.recipientEmail}
                updateState={setState}
                onBack={backHandler}
              />
            )}
          </PageContainer>
        </Elements>
      </StripeProvider>
    </PageWithAuth>
  );
};

export default GiftDropPage;
